import { apiClient } from './apiClient';
import { apiConfig } from '@/config/apiConfig';
import { handleServiceError } from '@/utils/errorHandler';
import {
  GalleryListResponse,
  GalleryDetailsResponse,
  GalleryOptionsResponse,
  CreateGalleryResponse,
  ApiResponse,
  GalleryListParams,
  CreateGalleryRequest,
  UpdateGalleryRequest,
  GalleryOptionsParams
} from '@/types/gallery';

export class GalleryService {
  private endpoints = apiConfig.endpoints.assetManagement.gallery;

  /**
   * Get paginated list of galleries with optional filters
   */
  async getGalleries(params: GalleryListParams = {}): Promise<GalleryListResponse> {
    try {
      const response = await apiClient.get(this.endpoints.list, { params });
      return response.data;
    } catch (error: any) {
      handleServiceError(error, 'Fetch Galleries');
    }
  }

  /**
   * Get detailed information about a specific gallery
   */
  async getGalleryDetails(galleryId: number): Promise<GalleryDetailsResponse> {
    try {
      const response = await apiClient.get(`${this.endpoints.details}/${galleryId}`);
      return response.data;
    } catch (error: any) {
      handleServiceError(error, 'Fetch Gallery Details');
    }
  }

  /**
   * Get available assets that can be added to a gallery
   */
  async getGalleryOptions(galleryId: number, params: GalleryOptionsParams = {}): Promise<GalleryOptionsResponse> {
    try {
      const response = await apiClient.get(`${this.endpoints.options}/${galleryId}`, { params });
      return response.data;
    } catch (error: any) {
      handleServiceError(error, 'Fetch Gallery Options');
    }
  }

  /**
   * Create a new gallery
   */
  async createGallery(galleryData: CreateGalleryRequest): Promise<CreateGalleryResponse> {
    try {
      const response = await apiClient.post(this.endpoints.create, galleryData);
      return response.data;
    } catch (error: any) {
      // Handle validation errors
      if (error.response?.status === 400) {
        const errorData = error.response.data;
        if (errorData.error && errorData.message) {
          throw new Error(errorData.message);
        }
      }
      
      throw new Error(error.response?.data?.message || 'Failed to create gallery');
    }
  }

  /**
   * Update an existing gallery's information and assets
   */
  async updateGallery(galleryData: UpdateGalleryRequest): Promise<ApiResponse> {
    try {
      const response = await apiClient.post(this.endpoints.update, galleryData);
      return response.data;
    } catch (error: any) {
      // Handle validation errors
      if (error.response?.status === 400) {
        const errorData = error.response.data;
        if (errorData.error && errorData.message) {
          throw new Error(errorData.message);
        }
      }
      
      throw new Error(error.response?.data?.message || 'Failed to update gallery');
    }
  }

  /**
   * Search galleries by query string
   */
  async searchGalleries(query: string, params: Partial<GalleryListParams> = {}): Promise<GalleryListResponse> {
    try {
      const searchParams = {
        ...params,
        village_name: query, // Search by village name
        page: params.page || 1,
        page_size: params.page_size || 10
      };
      
      return await this.getGalleries(searchParams);
    } catch (error: any) {
      handleServiceError(error, 'Search Galleries');
    }
  }

  /**
   * Get galleries filtered by location
   */
  async getGalleriesByLocation(locationId: string, params: Partial<GalleryListParams> = {}): Promise<GalleryListResponse> {
    try {
      const filterParams = {
        ...params,
        village_id: locationId
      };
      
      return await this.getGalleries(filterParams);
    } catch (error: any) {
      handleServiceError(error, 'Fetch Galleries by Location');
    }
  }

  /**
   * Get galleries filtered by program
   */
  async getGalleriesByProgram(programId: string, params: Partial<GalleryListParams> = {}): Promise<GalleryListResponse> {
    try {
      const filterParams = {
        ...params,
        program: programId as any
      };
      
      return await this.getGalleries(filterParams);
    } catch (error: any) {
      handleServiceError(error, 'Fetch Galleries by Program');
    }
  }

  /**
   * Update gallery status (activate/deactivate)
   */
  async updateGalleryStatus(galleryId: number, status: 'Active' | 'Inactive'): Promise<ApiResponse> {
    try {
      const updateData: UpdateGalleryRequest = {
        id: galleryId,
        status,
      };

      const response = await apiClient.post(this.endpoints.update, updateData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update gallery status');
    }
  }

  /**
   * Reorder assets in a gallery
   */
  async reorderGalleryAssets(galleryId: number, assetUpdates: Array<{ id: number; sequence_number: number }>): Promise<ApiResponse> {
    try {
      // Get current gallery details to preserve existing asset properties
      const galleryDetails = await this.getGalleryDetails(galleryId);
      const currentAssets = galleryDetails.data.assets;
      
      // Create a map for quick lookup of current asset properties
      const assetPropertiesMap = new Map(
        currentAssets.map(asset => [
          asset.id, 
          {
            asset_library_id: asset.asset_library_id,
            status: asset.status
          }
        ])
      );
      
      const updateData: UpdateGalleryRequest = {
        id: galleryId,
        assets: assetUpdates.map(asset => {
          const existingProperties = assetPropertiesMap.get(asset.id);
          
          return {
            id: asset.id,
            sequence_number: asset.sequence_number,
            asset_library_id: existingProperties?.asset_library_id || 0,
            status: existingProperties?.status || 'Active'
          };
        })
      };
      
      const response = await apiClient.post(this.endpoints.update, updateData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to reorder gallery assets');
    }
  }

  /**
   * Add assets to gallery
   */
  async addAssetsToGallery(galleryId: number, assetLibraryIds: number[]): Promise<ApiResponse> {
    try {
      // Get current gallery to determine next sequence numbers
      const galleryDetails = await this.getGalleryDetails(galleryId);
      const currentActiveAssets = galleryDetails.data.assets.filter(asset => asset.status === 'Active');
      const nextSequenceNumber = Math.max(...currentActiveAssets.map(a => a.sequence_number), 0) + 1;
      
      const newAssets = assetLibraryIds.map((assetLibraryId, index) => ({
        sequence_number: nextSequenceNumber + index,
        asset_library_id: assetLibraryId,
        status: 'Active' as const
      }));
      
      const updateData: UpdateGalleryRequest = {
        id: galleryId,
        assets: [...galleryDetails.data.assets.map(asset => ({
          id: asset.id,
          sequence_number: asset.sequence_number,
          asset_library_id: asset.asset_library_id,
          status: asset.status
        })), ...newAssets]
      };
      
      const response = await apiClient.post(this.endpoints.update, updateData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to add assets to gallery');
    }
  }

}

// Export singleton instance
export const galleryService = new GalleryService();
export default galleryService;